import { UserGroupSetIcon } from '@tableau/icons/UserGroupSetIcon';
import { UsersGroupIcon } from '@tableau/icons/UsersGroupIcon';
import { UserSingleBaseIcon } from '@tableau/icons/UserSingleBaseIcon';
import { Colors } from '@tableau/icons/util/Colors';
import { FilteringList, ListItem } from '@tableau/widgets';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import { Filter } from '~src/commonInfra/utils/Filter';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { SearchSuggestionsService } from '~src/spacesPlatform/services/SearchSuggestionsService';
import { Groups } from '~src/userManagement/groups/Groups';
import { isGroupSetsEnabledForSite } from '~src/userManagement/groupsets/selectors/GroupSetSessionInfoSelectors';
import { FilterParam, getAllGroupSets, IGetGroupSetDetailsResult, Operators, ResultLevel } from '~src/userManagement/groupsets/serverApi/requests/GetAllGroupSetsRequest';
import { UserNames } from '~src/userManagement/resources/UserNames';

import { Messages } from '../../codegen/Localize';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { GroupKeys, IGroup } from '../../userManagement/groups/serverApi/types/IGroup';
import { IGroupSetInfo } from '../../userManagement/groupsets/models/IGroupSetInfo';
import { IUser, IUserName, UserKeys } from '../../userManagement/users/serverApi/types/IUser';
import { PermissionsGridStyles } from '../styles/PermissionsGridStyle';
import { getLocalizedDomainName } from '../utils/DomainTypeLocalizationHelper';

export interface GranteeListItem extends ListItem {
  displayText?: string; // can be used to sort list items
  groupInfo?: IGroup;       // set if the list item is a group
  user?: IUser;               // set if the list item is a user
  groupSet?: IGroupSetInfo;  // set if the list item is a groupSet
}

export interface GranteeSearchFieldProps {
  onGranteeSelect: (group?: IGroup, user?: IUser, groupSet?: IGroupSetInfo) => void;
}

export interface GranteeSearchFieldState {
  searchResults: GranteeListItem[];
  searchText: string;
}

const GranteeSearchFieldDefaultState: GranteeSearchFieldState = Object.freeze({ searchResults: [], searchText: '' });

export class GranteeSearchField extends React.PureComponent<GranteeSearchFieldProps, GranteeSearchFieldState>{
  public static readonly DEBOUNCE_MILLISECONDS = 250;
  public static readonly INPUT_ID = 'grantee-search-field-input';
  public static readonly LIST_ID = 'grantee-search-result-list';
  public static readonly GROUP_ID_PREFIX = 'Group_';
  public static readonly USER_ID_PREFIX = 'User_';
  public static readonly GROUPSET_ID_PREFIX = 'GroupSet_';

  private isMouseOverDropdown: boolean = false; // tracking if interacting with the dropdown
  private dropdownClassName: string = 'showPopup';

  public state: GranteeSearchFieldState = { ...GranteeSearchFieldDefaultState };

  public componentDidMount() {
    this.undebouncedSearch(this.state.searchText);
  }

  public groupToGranteeListItem = (group: IGroup): GranteeListItem => {
    const content = (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchResultContainer}>
        <span className={PermissionsGridStyles.EditableGranteeCellSearchResultImgContainer}>
            <UsersGroupIcon size={14} color={Colors.Default} alt={Messages.group()} />
        </span>
        {group.name}
      </div>
    );
    return {
      content,
      displayText: group.name,
      groupInfo: group,
      tooltip: Messages.permissionsGranteeFullyQualifiedName_group({
        DOMAIN_NAME: getLocalizedDomainName(group.domainName),
        GROUP_NAME: group.name,
      }),
      user: undefined,
      value: `${GranteeSearchField.GROUP_ID_PREFIX + group.id}`,
    };
  };

  public userToGranteeListItem = (user: IUserName): GranteeListItem => {
    const content = (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchResultContainer}>
        <span className={PermissionsGridStyles.EditableGranteeCellSearchResultImgContainer}>
          <UserSingleBaseIcon size={14} color={Colors.Default} alt={Messages.user()} />
        </span>
        {user.displayName}
      </div>
    );
    return {
      content,
      displayText: user.displayName,
      groupInfo: undefined,
      tooltip: Messages.permissionsGranteeFullyQualifiedName_user({
        DISPLAY_NAME: user.displayName,
        DOMAIN_NAME: getLocalizedDomainName(user.domainName),
        USER_NAME: user.username,
      }),
      user,
      value: `${GranteeSearchField.USER_ID_PREFIX + user.id}`,
    };
  };

  public groupSetToGranteeListItem = (groupSet: IGroupSetInfo): GranteeListItem => {
    const content = (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchResultContainer}>
        <span className={PermissionsGridStyles.EditableGranteeCellSearchResultImgContainer}>
            <UserGroupSetIcon size={14} color={Colors.Default} alt={Messages.groupSet()}/>
        </span>
        {groupSet.name}
      </div>
    );
    return {
      content,
      displayText: groupSet.name,
      groupInfo: undefined,
      groupSet: groupSet,
      tooltip: Messages.permissionsGranteeFullyQualifiedName_groupSet({
        GROUPSET_NAME: groupSet.name,
      }),
      user: undefined,
      value: `${GranteeSearchField.GROUPSET_ID_PREFIX + groupSet.localId}`,
    };
  };

  // public for testing purposes
  public fetchSearchResults = (searchText: string): Promise<[ListResult<IGroup>, ListResult<IUserName>, ListResult<IGroup>, ListResult<IUserName>, IGetGroupSetDetailsResult]> => {
    // fetch the first 50 matching users/groups, as well as any exact matches
    const exactGroup: Promise<ListResult<IGroup>> = Groups.all().withFiltersAndOrder([Filter.stringFieldEq(GroupKeys.name, searchText)], [Order.asc(GroupKeys.name)]).withExtraParams({includeUserCount: false}).getSlice(0, 1);
    const exactUser: Promise<ListResult<IUserName>> = UserNames.all().withFiltersAndOrder([Filter.stringFieldEq(UserKeys.displayName, searchText)], [Order.asc(UserKeys.displayName)]).getSlice(0, 1);
    const groupQuery: Promise<ListResult<IGroup>> = SearchSuggestionsService.groups(searchText).getSlice(0, 50);
    const userQuery: Promise<ListResult<IUserName>> = SearchSuggestionsService.users(searchText).getSlice(0, 50);
    let groupSetQuery: Promise<IGetGroupSetDetailsResult> | undefined;
    if (isGroupSetsEnabledForSite()) {
      const filterByName: FilterParam  = {
        operator: 'AND',
        clauses: [
          {
            operator: Operators.matches,
            value: searchText
          }
        ]
      };
      groupSetQuery = getAllGroupSets({filter: filterByName, resultLevel:ResultLevel.LOCAL});
    }
    return Promise.all([groupQuery, userQuery, exactGroup, exactUser, groupSetQuery]);
  };

  private undebouncedSearch = async (searchText: string) => {
    const results = await this.fetchSearchResults(searchText);
    const groupListItems: GranteeListItem[] = results[0].items.map(this.groupToGranteeListItem);
    const userListItems: GranteeListItem[] = results[1].items.map(this.userToGranteeListItem);
    const exactGroup: GranteeListItem[] = results[2].items.map(this.groupToGranteeListItem);
    const exactUser: GranteeListItem[] = results[3].items.map(this.userToGranteeListItem);
    let filteredGroupSets: GranteeListItem[] = [];
    if (isGroupSetsEnabledForSite()) {
      filteredGroupSets = results[4].groupSets.groupSet.map(this.groupSetToGranteeListItem);
    }
    let filteredGroups: GranteeListItem[];
    let filteredUsers: GranteeListItem[];

    // the exact match should appear once in the search results, at the top.
    // so remove the exact match from the list of non-exact-match search results
    if (exactGroup !== undefined && exactGroup.length) {
      filteredGroups = groupListItems.filter((group) => {
        return group.groupInfo.id !== exactGroup[0].groupInfo.id;
      });
    } else {
      filteredGroups = groupListItems;
    }
    if (exactUser !== undefined && exactUser.length) {
      filteredUsers = userListItems.filter((user) => {
        return user.user.id !== exactUser[0].user.id;
      });
    } else {
      filteredUsers = userListItems;
    }

    const sortedListItems: GranteeListItem[] = [...filteredGroups, ...filteredUsers, ...filteredGroupSets].sort((a, b) => (a.displayText > b.displayText) ? 1 : -1);
    const totalItems: GranteeListItem[] = [...(exactGroup !== undefined ? exactGroup : []), ...(exactUser !== undefined ? exactUser : []), ...sortedListItems];
    if (searchText === this.state.searchText) {
      // search text could have changed before the promises resolved, so only update the state if results are still relevant
      this.setState({
        searchResults: totalItems.length === 0 ? [{ value: Messages.noMatchingResults(), disabled: true }] : totalItems,
      });
    }
  };

  private debouncedSearch = _.debounce(this.undebouncedSearch, GranteeSearchField.DEBOUNCE_MILLISECONDS);

  private handleFilterChange = (searchText: string): void => {
    if (searchText) {
      this.setState({ searchText });
      this.debouncedSearch(searchText);
    } else {
      this.setState(GranteeSearchFieldDefaultState);
    }
  };

  private handleSelect = (value: string): void => {
    const selectedGranteeListItem = this.state.searchResults.find(item => item.value === value);
    this.props.onGranteeSelect(selectedGranteeListItem.groupInfo, selectedGranteeListItem.user, selectedGranteeListItem.groupSet);
  };

  private handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    // The purpose of this callback is to clear the widget input box when changing focus to something else on the screen.
    // But there are exceptions - when interacting with anything inside the dropdown or with the dropdown itself (which can
    // happen during scrolling - TFS 1065910), the state should not be cleared.
    const elementReceivingFocus = (event.relatedTarget || document.activeElement) as HTMLElement;
    if (this.isMouseOverDropdown || this.isInsideDropdown(elementReceivingFocus)) {
      return;
    }
    this.setState(GranteeSearchFieldDefaultState);
  };

  private handleMouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    const mousedOverElement = event.target as HTMLElement;
    if (this.isInsideDropdown(mousedOverElement)) {
      this.isMouseOverDropdown = true;
    }
  };

  private handleMouseUp = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    const mousedOverElement = event.target as HTMLElement;
    if (this.isInsideDropdown(mousedOverElement)) {
      this.isMouseOverDropdown = false;
      // when done interacting with the dropdown, focus should go back to the widget input
      const inputElement = (ReactDOM.findDOMNode(this) as HTMLElement).getElementsByTagName('input').item(0);
      inputElement && inputElement.focus();
    }
  };

  private isInsideDropdown = (elementToCheck: HTMLElement): boolean => {
    const dropdownElement = (ReactDOM.findDOMNode(this) as HTMLElement).getElementsByClassName(this.dropdownClassName).item(0);
    // check that both elements exist
    if (dropdownElement && elementToCheck) {
      return dropdownElement.contains(elementToCheck);
    }
    return false;
  };

  public render() {
    const className = `${PermissionsGridStyles.EditableGranteeCellSearchField} ${this.state.searchResults.length > 0 && this.dropdownClassName}`;
    /* eslint-disable jsx-a11y/no-autofocus */
    return (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchFieldContainer}>
        <FilteringList
          label={Messages.searchForGroupOrUser()}
          autoFocus={true}
          className={className}
          inputTestId={GranteeSearchField.INPUT_ID}
          items={this.state.searchResults}
          listTestId={GranteeSearchField.LIST_ID}
          onBlur={this.handleBlur}
          onFilterChange={this.handleFilterChange}
          onMouseDown={this.handleMouseDown}
          onMouseUp={this.handleMouseUp}
          onSelect={this.handleSelect}
          searchPlaceholder={Messages.searchForGroupOrUser()}
          searchText={this.state.searchText}
        />
      </div>
    );
  }
}
